import { Button, generateResolver, Modal, Notification, STATUS_TYPES, yup } from "dyl-components";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Form, Grid, Header, Segment } from "semantic-ui-react";
import PriceField from "shared/forms/ProductForm/PriceField";
import BankPaymentMethod from "shared/Payment/BankPaymentMethod";
import { BulkActionsContext } from "shared/context/BulkActionsProvider";
import applyCurrencySchema from "shared/schemas/products/applyCurrencySchema";
import paymentActions from "actions/payment";
import { useContext, useEffect, useRef } from "react";
import OutstandingInvoicesTable from "shared/OutstandingInvoicesTable";
import accountActions from "actions/account";
import { hostedProfileSettings } from "shared/Payment/hostedProfileSettings";

import subscriptionActions from "actions/subscription";

const OutstandingInvoicesForm = ({ isOpen, onClose, formTitle, customer_id, opportunity_id, refresh }) => {
    const [invoicesToPay, setInvoicesToPay] = useContext(BulkActionsContext);
    const { invoices } = useSelector(state => ({
        invoices: state.subscription.outstanding_invoices
    }));
    const payElement = useRef();

    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            amount: 0,
            fundToApply: null,
        },
        resolver: generateResolver({
            amount: applyCurrencySchema(yup.number()).required("This field is required"),
            payment_method: yup.string().required("This field is required"),
        }),
    });

    const {
        formState: { isValid, isDirty },
        handleSubmit,
        reset,
    } = methods;

    const isProcessingPaymentDetails = useSelector(state => state.account.customerBeingReactivated || state.payment.isPayingInvoices);
    const paymentMethods = useSelector(state => state.payment.profile?.paymentProfiles || []);

    const customer_profile_id = useSelector(
        (state) => state.payment.profile?.customerProfileId
    );

    const dispatch = useDispatch();

    const onProcess = async (data) => {
        const paymentInfo = paymentMethods.find(paymentMethod => data.payment_method === paymentMethod.customerPaymentProfileId);
        const { creditCard, bankAccount } = paymentInfo.payment;
        const payment = (() => {
            if (creditCard) {
                const { cardType, issuerNumber, ...otherInfo } = creditCard;
                return {
                    creditCard: otherInfo
                };
            }
            return {
                bankAccount
            };
        })();
        try {
            const payload = {
                customer_profile_id,
                customerPaymentProfileId: data.payment_method,
                paymentInfo: payment,
                amount: data.amount,
                processor: "authorize.net"
            }
            await dispatch(paymentActions.payInvoices(customer_id, payload, { invoicesToPay }));
            const allInvoicesArePaid = invoicesToPay.length === invoices.length;
            if (allInvoicesArePaid) {
                await dispatch(
                    accountActions.reactivateCustomer(customer_id, {
                        fund_type: (() => {
                            const term = data.term;
                            if (term?.includes("trial")) {
                                return "trial";
                            }
                            if (term === "demo") {
                                return term;
                            }
                            return "funded";
                        })(),
                        pipeline_id: opportunity_id
                    })
                )
            }
            Notification.alert("Successfully reactivated customer!", STATUS_TYPES.SUCCESS);
            reset();
            setInvoicesToPay([]);
            onClose();
            if (refresh) {
                refresh();
            }
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to reactivate customer", STATUS_TYPES.ERROR);
        }
    };

    const { isReadingPaymentInfo } = useSelector((state) => ({
        isReadingPaymentInfo:
            state.payment.isReadingCustomerFormToken ||
            state.payment.isReadingCustomerProfile,
    }));

    useEffect(() => {
        if (isOpen) {
            dispatch(paymentActions.resetFormToken());
            dispatch(paymentActions.getPaymentMethods(customer_id)).then((response) => {
                if (!response.profile.paymentProfiles) {
                    dispatch(
                        paymentActions.generateAcceptCustomerFormToken(
                            {
                                hostedProfileSettings: hostedProfileSettings
                            },
                            null,
                            customer_id
                        )
                    ).then(() => {
                        payElement.current?.submit({ target: "add_payment" });
                    });
                }
            });
            dispatch(subscriptionActions.getOutstandingInvoices(customer_id));
        }
    }, [isOpen, customer_id, dispatch])

    return (
        <Modal
            open={isOpen}
            onClose={() => {
                onClose();
                reset();
                setInvoicesToPay([]);
            }}
            size="big"
        >
            <Modal.Header>{formTitle}</Modal.Header>
            <Modal.Content scrolling>
                <FormProvider {...methods}>
                    <Form loading={isProcessingPaymentDetails}>
                        <Grid>
                            <Grid.Row>
                                <Grid.Column>
                                    <Header color="primary">
                                        Outstanding Invoice
                                    </Header>
                                    <OutstandingInvoicesTable />
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row>
                                <Grid.Column>
                                    <Header color="primary">
                                        Payment Method
                                    </Header>
                                    {!isReadingPaymentInfo ? (
                                        <BankPaymentMethod
                                            account_id={customer_id}
                                            payElement={payElement}
                                        />
                                    ) : (
                                        <Segment loading basic />
                                    )}
                                </Grid.Column>
                            </Grid.Row>
                            <Grid.Row columns="2">
                                <Grid.Column>
                                    <PriceField
                                        label={
                                            <Header color="primary">
                                                Amount
                                            </Header>
                                        }
                                        name={"amount"}
                                        disabled
                                    />
                                </Grid.Column>
                            </Grid.Row>
                        </Grid>
                    </Form>
                </FormProvider>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    disabled={
                        !isValid || !isDirty || isProcessingPaymentDetails
                    }
                    type="submit"
                    color="primary"
                    onClick={handleSubmit(onProcess)}
                    loading={isProcessingPaymentDetails}
                >
                    Process
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default OutstandingInvoicesForm;
